import React, { useState } from 'react';
import ExclamationIcon from '@heroicons/react/outline/ExclamationIcon.js';
import PlusIcon from '@heroicons/react/outline/PlusIcon.js';
import AlignCenterIcon from 'remixicon-react/AlignCenterIcon.js';
import AlignLeftIcon from 'remixicon-react/AlignLeftIcon.js';
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n';
import { RichTextPickerField, LinkFieldPicker, ImagePickerBlockField, } from '@tribeplatform/react-components/PickerField';
import { Button, ButtonGroup } from '@tribeplatform/react-ui-kit/Button';
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl';
import { Select } from '@tribeplatform/react-ui-kit/Select';
import { Slider } from '@tribeplatform/react-ui-kit/Slider';
import { SortableList } from '@tribeplatform/react-ui-kit/SortableList';
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip';
import { useBlock } from '../../hooks/index.js';
import { defaultQuickLinksItemsFactory } from './constants.js';
import { ActionTypes } from './types.js';
export const QuickLinksBlockSettings = () => {
    const { $t } = useI18n();
    const { block: { props: { title, items, alignment, horizontalScroll, actionType, gridCols, }, }, upsertProp, } = useBlock();
    const [activeIndex, setActiveIndex] = useState(0);
    const defaultQuickLinksItems = useStaticIntl(defaultQuickLinksItemsFactory);
    const addItem = () => {
        const newItems = [
            ...items,
            Object.assign({}, defaultQuickLinksItems[0]),
        ];
        const index = newItems.length - 1;
        upsertProp('items', newItems);
        setActiveIndex(index);
    };
    const deleteItem = (items, key, itemIndex) => {
        upsertProp(key, [
            ...items.slice(0, itemIndex),
            ...items.slice(itemIndex + 1),
        ]);
    };
    const editItem = (index, key, value) => {
        const newItems = items.map((item, idx) => {
            if (idx === index) {
                return Object.assign(Object.assign({}, item), { [key]: value });
            }
            return item;
        });
        upsertProp(`items`, newItems);
        setActiveIndex(index);
    };
    const onDragEnd = (items, key) => result => {
        if (!result.destination) {
            return;
        }
        const newItems = SortableList.reorder(items, result.source.index, result.destination.index);
        upsertProp(key, newItems);
    };
    return (React.createElement("div", { className: "space-y-5" },
        React.createElement(FormControl.Input, { name: "title", label: React.createElement(T, { id: "Generics.Title", defaultMessage: "Title" }), placeholder: $t({
                id: 'Blocks.QuickLinks.TitlePlaceholder',
                defaultMessage: 'Add a title',
            }), value: title, onChange: e => upsertProp('title', e.target.value) }),
        React.createElement(FormControl.Select, { name: "actionType", value: actionType, onChange: (value) => {
                upsertProp('actionType', value);
                return value;
            }, label: React.createElement(T, { id: "Generics.Style", defaultMessage: "Style" }) },
            React.createElement(Select.Button, null, ActionTypes[actionType]),
            React.createElement(Select.Items, null,
                React.createElement(Select.Item, { value: "button" },
                    React.createElement(T, { id: "Generics.Button", defaultMessage: "Button" })),
                React.createElement(Select.Item, { value: "link" },
                    React.createElement(T, { id: "Generics.Link", defaultMessage: "Link" })),
                React.createElement(Select.Item, { value: "clickable-card" },
                    React.createElement(T, { id: "Blocks.QuickLinks.ClickableCard", defaultMessage: "Clickable card" })))),
        React.createElement(FormControl, null,
            React.createElement(FormControl.Label, null,
                React.createElement(T, { id: "Generics.Alignment", defaultMessage: "Alignment" })),
            React.createElement(ButtonGroup, { className: "shadow-none" },
                React.createElement(Button, { disabled: alignment === 'left', onClick: () => upsertProp('alignment', 'left') },
                    React.createElement(AlignLeftIcon, { className: "w-4 h-4" })),
                React.createElement(Button, { disabled: alignment === 'center', onClick: () => upsertProp('alignment', 'center') },
                    React.createElement(AlignCenterIcon, { className: "w-4 h-4" })))),
        React.createElement(FormControl.Toggle, { name: "horizontalScroll", label: React.createElement("div", { className: "flex space-s-1 items-center" },
                React.createElement("span", null,
                    React.createElement(T, { id: "Blocks.QuickLinks.HorizontalScroll", defaultMessage: "Horizontal scroll" })),
                React.createElement(Tooltip, { placement: "top" },
                    React.createElement(Tooltip.Trigger, null,
                        React.createElement(ExclamationIcon, { className: "h-4 w-4" })),
                    React.createElement(Tooltip.Panel, null,
                        React.createElement(T, { id: "Blocks.QuickLinks.HorizontalScrollTooltip", defaultMessage: "You cannot test scrolling while you are in the editor" })))), checked: horizontalScroll, onChange: value => {
                upsertProp('horizontalScroll', value);
            } }),
        React.createElement(FormControl, null,
            React.createElement(FormControl.Label, null,
                React.createElement(T, { id: "Blocks.QuickLinks.GridSize", defaultMessage: "Grid size" })),
            React.createElement(Slider, { value: gridCols, step: 1, min: 2, max: 5, onChange: value => {
                    upsertProp('gridCols', value);
                } })),
        React.createElement(SortableList, { onDragEnd: onDragEnd(items, 'items'), items: items.map((it, index) => (Object.assign(Object.assign({}, it), { id: index }))), renderItem: (item, index) => (React.createElement(SortableList.Item, { id: item.id }, index === activeIndex ? (React.createElement("div", { className: "space-y-3 bg-surface-subdued p-3 rounded-md" },
                React.createElement("div", { className: "flex items-center space-s-2" },
                    React.createElement(SortableList.Handle, null),
                    React.createElement("span", { className: "truncate" }, item.title || (React.createElement(T, { id: "Blocks.QuickLinks.DefaultQuickLinkTitle", defaultMessage: "Quick link title" })))),
                React.createElement(FormControl, null,
                    React.createElement(FormControl.Label, null,
                        React.createElement(T, { id: "Generics.Image", defaultMessage: "Image" })),
                    React.createElement(ImagePickerBlockField, { image: item.image, removable: true, onChange: image => {
                            editItem(index, 'image', image);
                        } })),
                React.createElement(FormControl.Input, { name: "title", label: React.createElement(T, { id: "Generics.Title", defaultMessage: "Title" }), value: item.title, onChange: e => editItem(index, 'title', e.target.value) }),
                React.createElement(RichTextPickerField, { label: React.createElement(T, { id: "Generics.Description", defaultMessage: "Description" }), value: item.description, name: "description", onChange: value => {
                        editItem(index, 'description', value);
                    } }),
                actionType !== 'clickable-card' && (React.createElement(FormControl.Input, { name: "action.text", label: actionType === 'button'
                        ? $t({
                            id: 'Blocks.QuickLinks.ButtonTextLabel',
                            defaultMessage: 'Button text',
                        })
                        : $t({
                            id: 'Blocks.QuickLinks.LinkTextLabel',
                            defaultMessage: 'Link text',
                        }), value: item.action.text, onChange: e => editItem(index, 'action', Object.assign(Object.assign({}, item.action), { text: e.target.value })) })),
                React.createElement(LinkFieldPicker, { onChange: (href, openInNewWindow) => {
                        if (item.action.href === href) {
                            editItem(index, 'action', Object.assign(Object.assign({}, item.action), { openInNewWindow }));
                        }
                        else {
                            editItem(index, 'action', Object.assign(Object.assign({}, item.action), { href }));
                        }
                    }, name: "action.href", label: "URL", value: item.action.href, openInNewWindow: item.action.openInNewWindow }),
                React.createElement(Button, { variant: "outline", onClick: () => deleteItem(items, 'items', index) },
                    React.createElement(T, { id: "Generics.Remove", defaultMessage: "Remove" })))) : (React.createElement("div", { className: "bg-surface-subdued p-3 rounded-md cursor-pointer flex items-center space-s-2", onClick: () => setActiveIndex(index) },
                React.createElement(SortableList.Handle, null),
                React.createElement("span", { className: "truncate" }, item.title || (React.createElement(T, { id: "Blocks.QuickLinks.DefaultQuickLinkTitle", defaultMessage: "Quick link title" }))))))) }),
        React.createElement(Button, { variant: "outline", onClick: () => addItem(), fullWidth: true, leadingIcon: React.createElement(PlusIcon, null) },
            React.createElement(T, { id: "Blocks.QuickLinks.AddQuickLinkButtonText", defaultMessage: "Add quick link" }))));
};
//# sourceMappingURL=Settings.js.map